import { client } from "utils/axios";

export async function getEntradaCorteByPedidoId(pedidoId) {
  if (!pedidoId) {
    return null;
  }

  try {
    const { data } = await client.get(
      `/entradaCorte/pedido/${pedidoId}`
    );

    return data;
  } catch (error) {
    console.error(
      "Error al consultar entradaCorte por pedido:",
      error?.response?.status,
      error?.response?.data || error
    );
    return null;
  }
}

export async function createEntradaCorte(payload) {
  try {
    const { data } = await client.post("/entradaCorte", payload);
    return data;
  } catch (error) {
    console.error(
      "Error al crear entradaCorte:",
      error?.response?.status,
      error?.response?.data || error
    );
    throw error;
  }
}

export function calculateSalidaCorteProgress(salida, entradaSummary) {
  const totalAsignado = (salida?.details?.data || []).reduce(
    (sum, item) => sum + Number(item?.cantidadAsignada || 0),
    0
  );
  const totalEntregado = Number(entradaSummary?.total || 0);

  const percentage =
    totalAsignado > 0
      ? Number(Math.min((totalEntregado / totalAsignado) * 100, 100).toFixed(2))
      : 0;

  const completed = totalAsignado > 0 && totalEntregado >= totalAsignado;

  return {
    totalAsignado,
    totalEntregado,
    pendiente: Number(Math.max(totalAsignado - totalEntregado, 0).toFixed(2)),
    percentage,
    completed,
  };
}

export async function updateSalidaCortePercentage(salida, entradaSummary) {
  const progress = calculateSalidaCorteProgress(salida, entradaSummary);
  const detailsData = (salida?.details?.data || []).map((detail) => {
    const prendas = (detail?.prendas || []).map((prenda) => {
      const recolectadaPrenda = Number(
        getRecolectadaByTallaPrenda(entradaSummary, detail?.talla, prenda) || 0
      );

      return {
        ...prenda,
        total_pickup: recolectadaPrenda,
      };
    });

    const totalPickupDetail =
      prendas.length > 0
        ? prendas.reduce((sum, prenda) => sum + Number(prenda.total_pickup || 0), 0)
        : Number(getRecolectadaByTalla(entradaSummary, detail?.talla) || 0);

    return {
      ...detail,
      prendas,
      total_pickup: Number(totalPickupDetail.toFixed(2)),
    };
  });

  const payload = {
    ...salida,
    details: {
      ...(salida?.details || {}),
      data: detailsData,
    },
    percentage: progress.percentage,
    completed: progress.completed,
  };

  try {
    const { data } = await client.put("/salidaCorte", payload);
    return { data, progress };
  } catch (error) {
    console.error(
      "Error al actualizar porcentaje de salidaCorte:",
      error?.response?.status,
      error?.response?.data || error
    );
    throw error;
  }
}

export function filterEntradaCorteByFolio(payload, folio) {
  if (!payload || folio == null) {
    return [];
  }

  const folioStr = String(folio).trim();
  let data = [];

  if (Array.isArray(payload)) {
    data = payload;
  } else if (Array.isArray(payload.data)) {
    data = payload.data;
  } else {
    data = [payload];
  }

  return data.filter((item) => {
    const folioCandidatos = [
      item?.folio,
      item?.salidaCorte?.folio,
      item?.salida?.folio,
      item?.salidaCorteFolio,
    ];

    return folioCandidatos.some(
      (value) => value != null && String(value).trim() === folioStr
    );
  });
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function pushUnique(arr, value) {
  if (!value) return;
  if (!arr.includes(value)) arr.push(value);
}

function buildIdKey(value) {
  if (value == null || value === "") return null;
  return `id:${String(value).trim()}`;
}

function buildNameKey(value) {
  const normalized = normalizeText(value);
  if (!normalized) return null;
  return `name:${normalized}`;
}

function getTallaKeys(item) {
  const keys = [];

  pushUnique(keys, buildIdKey(item?.talla?.id));
  pushUnique(keys, buildIdKey(item?.tallaId));

  if (typeof item?.talla === "string") {
    pushUnique(keys, buildNameKey(item.talla));
  }
  pushUnique(keys, buildNameKey(item?.talla?.nombre));
  pushUnique(keys, buildNameKey(item?.tallaNombre));
  pushUnique(keys, buildNameKey(item?.nombreTalla));

  return keys;
}

function getPrendaKeys(item) {
  const keys = [];

  pushUnique(keys, buildIdKey(item?.id));
  pushUnique(keys, buildIdKey(item?.prendaId));
  pushUnique(keys, buildIdKey(item?.productoId));
  pushUnique(keys, buildIdKey(item?.prenda?.id));
  pushUnique(keys, buildIdKey(item?.producto?.id));

  if (typeof item?.prenda === "string") {
    pushUnique(keys, buildNameKey(item.prenda));
  }
  if (typeof item?.producto === "string") {
    pushUnique(keys, buildNameKey(item.producto));
  }
  pushUnique(keys, buildNameKey(item?.nombre));
  pushUnique(keys, buildNameKey(item?.prenda?.nombre));
  pushUnique(keys, buildNameKey(item?.producto?.nombre));

  return keys;
}

function getCantidadFromItem(item) {
  return toNumber(
    item?.cantidadRecolectada ??
      item?.cantidad ??
      item?.cantidadEntrada ??
      item?.cantidadAsignada ??
      item?.piezas
  );
}

function getRowsFromEntradaItem(entradaItem) {
  if (Array.isArray(entradaItem?.details?.data)) return entradaItem.details.data;
  if (Array.isArray(entradaItem?.detalle?.data)) return entradaItem.detalle.data;
  if (Array.isArray(entradaItem?.details)) return entradaItem.details;
  if (Array.isArray(entradaItem?.data)) return entradaItem.data;
  return [entradaItem];
}

export function buildEntradaRecolectadaSummary(entradaCorteFiltrada) {
  const byTallaPrenda = {};
  const byTalla = {};
  const byPrenda = {};
  let total = 0;
  let rowsCount = 0;

  (entradaCorteFiltrada || []).forEach((entradaItem) => {
    const rows = getRowsFromEntradaItem(entradaItem);
    rows.forEach((row) => {
      const tallaKeys = getTallaKeys(row);
      if (tallaKeys.length === 0) return;

      if (Array.isArray(row?.prendas) && row.prendas.length > 0) {
        row.prendas.forEach((prendaRow) => {
          const prendaKeys = getPrendaKeys(prendaRow);
          if (prendaKeys.length === 0) return;
          const cantidad = getCantidadFromItem(prendaRow);

          tallaKeys.forEach((tallaKey) => {
            if (!byTallaPrenda[tallaKey]) {
              byTallaPrenda[tallaKey] = {};
            }
            byTalla[tallaKey] = toNumber(byTalla[tallaKey]) + cantidad;
            prendaKeys.forEach((prendaKey) => {
              byTallaPrenda[tallaKey][prendaKey] =
                toNumber(byTallaPrenda[tallaKey][prendaKey]) + cantidad;
            });
          });

          prendaKeys.forEach((prendaKey) => {
            byPrenda[prendaKey] = toNumber(byPrenda[prendaKey]) + cantidad;
          });

          total += cantidad;
          rowsCount += 1;
        });
        return;
      }

      const prendaKeys = getPrendaKeys(row);
      const cantidad = getCantidadFromItem(row);

      tallaKeys.forEach((tallaKey) => {
        if (!byTallaPrenda[tallaKey]) {
          byTallaPrenda[tallaKey] = {};
        }
        byTalla[tallaKey] = toNumber(byTalla[tallaKey]) + cantidad;

        prendaKeys.forEach((prendaKey) => {
          byTallaPrenda[tallaKey][prendaKey] =
            toNumber(byTallaPrenda[tallaKey][prendaKey]) + cantidad;
        });
      });

      prendaKeys.forEach((prendaKey) => {
        byPrenda[prendaKey] = toNumber(byPrenda[prendaKey]) + cantidad;
      });

      total += cantidad;
      rowsCount += 1;
    });
  });

  return {
    byTallaPrenda,
    byTalla,
    byPrenda,
    total: Number(total.toFixed(2)),
    rowsCount,
  };
}

export function getRecolectadaByTallaPrenda(summary, tallaValue, prendaValue) {
  const tallaKeys = getTallaKeys({ talla: tallaValue, tallaId: tallaValue?.id });
  const prendaKeys = getPrendaKeys({
    id: prendaValue?.id,
    prendaId: prendaValue?.prendaId,
    productoId: prendaValue?.productoId,
    prenda: prendaValue,
    producto: prendaValue,
    nombre: prendaValue?.nombre,
  });

  for (const tallaKey of tallaKeys) {
    const byPrendaMap = summary?.byTallaPrenda?.[tallaKey];
    if (!byPrendaMap) continue;
    for (const prendaKey of prendaKeys) {
      if (hasOwn(byPrendaMap, prendaKey)) {
        return Number(byPrendaMap[prendaKey] || 0);
      }
    }
  }

  return null;
}

export function getRecolectadaByTalla(summary, tallaValue) {
  const tallaKeys = getTallaKeys({ talla: tallaValue, tallaId: tallaValue?.id });

  for (const tallaKey of tallaKeys) {
    if (hasOwn(summary?.byTalla, tallaKey)) {
      return Number(summary.byTalla[tallaKey] || 0);
    }
  }

  return null;
}

export function hasRecolectadaDirectaEnTalla(summary, tallaValue) {
  const tallaKeys = getTallaKeys({ talla: tallaValue, tallaId: tallaValue?.id });

  for (const tallaKey of tallaKeys) {
    const byPrendaMap = summary?.byTallaPrenda?.[tallaKey];
    if (byPrendaMap && Object.keys(byPrendaMap).length > 0) {
      return true;
    }
  }

  return false;
}
